import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Alert from '@material-ui/lab/Alert';
import {
  StatusError,
  StatusOK,
  StatusPending,
  StatusWarning,
} from '@backstage/core-components';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { yardstickApiRef } from '@internal/yardstick-react';
import { useAsync } from 'react-use';
import { TaskStatus, YardstickIssueResult } from '@internal/yardstick-common';

const PROD_DEFAULT_ENTITY_REF =
  'component:principalinformationservices-emu/principal-developer-portal';
const LOCAL_DEFAULT_ENTITY_REF =
  'component:principalsbx-emu/principal-developer-portal';

const resolveDefaultEntityRef = (): string => {
  if (typeof window === 'undefined') {
    return PROD_DEFAULT_ENTITY_REF;
  }

  const hostname = window.location.hostname.toLowerCase();

  if (
    hostname === 'localhost' ||
    hostname.startsWith('127.') ||
    hostname.endsWith('.local')
  ) {
    return LOCAL_DEFAULT_ENTITY_REF;
  }

  return PROD_DEFAULT_ENTITY_REF;
};

type MeasurementOption = {
  id: string;
  title: string;
  domainId?: string;
  checks: { id: string; displayName: string }[];
};

type RunContext = {
  entityRef: string;
  measurementId: string;
};

type TaskStatusInfo = {
  id: string;
  status: TaskStatus;
  entityRef?: string;
  measurements?: string[];
  startedAt?: string;
  updatedAt?: string;
  expiresAt?: string;
};

type MeasurementCheckSummary = {
  id: string;
  name: string;
  value?: string;
  message?: string;
  issuesCount: number;
};

type MeasurementRunSummary = {
  measurementId: string;
  measurementName: string;
  measurementCategory: string;
  entityRef: string;
  runAt?: string;
  passed?: boolean | null;
  checks: MeasurementCheckSummary[];
  issues: YardstickIssueResult[];
  missingData?: boolean;
};

const formatDateTime = (value?: string): string => {
  if (!value) {
    return 'N/A';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

const renderTaskStatusLabel = (status?: TaskStatus) => {
  if (!status) {
    return <StatusWarning>unknown</StatusWarning>;
  }

  switch (status) {
    case 'completed':
      return <StatusOK>completed</StatusOK>;
    case 'submitted':
    case 'processing':
      return <StatusPending>{status}</StatusPending>;
    case 'error':
      return <StatusError>error</StatusError>;
    case 'expired':
      return <StatusWarning>expired</StatusWarning>;
    default:
      return <StatusWarning>{status}</StatusWarning>;
  }
};

const renderOutcomeLabel = (passed?: boolean | null) => {
  if (passed === true) {
    return <StatusOK>Measurement passed</StatusOK>;
  }

  if (passed === false) {
    return <StatusError>Measurement failed</StatusError>;
  }

  return <StatusWarning>Outcome unavailable</StatusWarning>;
};

const getCheckStatusLabel = (
  value?: string,
): { label: string; color: string } => {
  switch (value) {
    case 'passed':
      return { label: 'Passed', color: '#1f7a1f' };
    case 'failed':
      return { label: 'Failed', color: '#c62828' };
    case 'skipped':
      return { label: 'Skipped', color: '#6d4c41' };
    case 'unknown':
      return { label: 'Unknown', color: '#616161' };
    case 'error':
      return { label: 'Error', color: '#c62828' };
    default:
      return { label: 'N/A', color: '#616161' };
  }
};

const MAX_ISSUES_TO_SHOW = 5;

export const YardstickMeasurementRunner = (): ReactElement => {
  const catalogApi = useApi(catalogApiRef);
  const yardstickApi = useApi(yardstickApiRef);

  const [selectedEntityRef, setSelectedEntityRef] = useState<string>(
    resolveDefaultEntityRef,
  );
  const [selectedMeasurementId, setSelectedMeasurementId] = useState('');
  const [taskStatus, setTaskStatus] = useState<TaskStatusInfo | null>(null);
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const [pendingContext, setPendingContext] = useState<RunContext | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [resultSummary, setResultSummary] =
    useState<MeasurementRunSummary | null>(null);
  const [resultFetching, setResultFetching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    value: catalogEntities,
    loading: catalogLoading,
    error: catalogError,
  } = useAsync(async () => {
    const { items } = await catalogApi.getEntities({
      filter: [{ kind: 'Component' }],
      fields: [
        'kind',
        'metadata.name',
        'metadata.namespace',
        'metadata.title',
        'metadata.annotations',
        'spec.owner',
      ],
    });

    return items;
  }, [catalogApi]);

  const entityOptions = useMemo(
    () =>
      (catalogEntities ?? [])
        .map(entity => ({
          ref: stringifyEntityRef(entity),
          label: `${entity.metadata.namespace ?? 'default'}/${
            entity.metadata.name
          }`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [catalogEntities],
  );

  const entityLabel = useCallback(
    (entityRef: string) =>
      entityOptions.find(option => option.ref === entityRef)?.label ??
      entityRef,
    [entityOptions],
  );

  const {
    value: measurements,
    loading: measurementsLoading,
    error: measurementsError,
  } = useAsync(async () => {
    const data = await yardstickApi.getMeasurements();
    return data;
  }, [yardstickApi]);

  const measurementOptions = useMemo<MeasurementOption[]>(() => {
    if (!measurements) {
      return [];
    }

    return measurements.map(measurement => ({
      id: measurement.id,
      title: measurement.title ?? measurement.id,
      domainId: measurement.domain_id,
      checks: (measurement.checks ?? []).map(checkId => ({
        id: checkId,
        displayName: checkId,
      })),
    }));
  }, [measurements]);

  const measurementMap = useMemo(
    () =>
      new Map<string, MeasurementOption>(
        measurementOptions.map(option => [option.id, option]),
      ),
    [measurementOptions],
  );

  const selectedMeasurement = selectedMeasurementId
    ? measurementMap.get(selectedMeasurementId)
    : undefined;

  const canRunMeasurement =
    Boolean(selectedEntityRef && selectedMeasurementId) &&
    !isSubmitting &&
    !measurementsLoading;

  const fetchMeasurementOutcome = useCallback(
    async (context: RunContext) => {
      setResultFetching(true);
      try {
        const results = await yardstickApi.getMeasurementResults({
          entities: [context.entityRef],
          measurements: [context.measurementId],
          includeDetails: true,
          adminShowIssues: true,
        });

        const entityResult = results?.data?.[context.entityRef];
        const measurementResult = entityResult?.measurementResults?.find(
          result => result.measurement?.id === context.measurementId,
        );

        const measurementOption = measurementMap.get(context.measurementId);

        if (!measurementResult) {
          setResultSummary({
            measurementId: context.measurementId,
            measurementName: measurementOption?.title ?? context.measurementId,
            measurementCategory: measurementOption?.domainId ?? 'Uncategorized',
            entityRef: context.entityRef,
            passed: null,
            checks: [],
            issues: [],
            missingData: true,
          });
          setRunError(
            'No measurement result was returned. Verify that the measurement applies to the selected entity.',
          );
          return;
        }

        const measurementValue = measurementResult.value;

        const summary: MeasurementRunSummary = {
          measurementId:
            measurementResult.measurement?.id ?? context.measurementId,
          measurementName:
            measurementOption?.title ??
            measurementResult.measurement?.title ??
            measurementResult.measurement?.id ??
            context.measurementId,
          measurementCategory:
            measurementOption?.domainId ??
            measurementResult.measurement?.domain_id ??
            'Uncategorized',
          entityRef: context.entityRef,
          runAt: measurementResult.runAt,
          passed:
            typeof measurementValue === 'boolean'
              ? measurementValue
              : measurementValue === 'passed',
          checks:
            measurementResult.checkResults?.map(checkResult => ({
              id: checkResult.check?.id ?? '',
              name:
                measurementOption?.checks.find(
                  check => check.id === checkResult.check?.id,
                )?.displayName ??
                checkResult.check?.title ??
                checkResult.check?.id ??
                'Unknown check',
              value:
                typeof checkResult.value === 'string'
                  ? checkResult.value
                  : undefined,
              message: checkResult.message,
              issuesCount: checkResult.issues?.length ?? 0,
            })) ?? [],
          issues: measurementResult.issues ?? [],
        };

        setResultSummary(summary);
        setRunError(null);
      } catch (error) {
        setRunError(
          error instanceof Error
            ? error.message
            : 'Failed to fetch measurement results after refresh.',
        );
      } finally {
        setResultFetching(false);
      }
    },
    [yardstickApi, measurementMap],
  );

  const handleRunMeasurement = useCallback(async () => {
    if (!selectedMeasurementId) {
      setRunError('Select a measurement before running Yardstick.');
      return;
    }

    setIsSubmitting(true);
    setRunError(null);
    setResultSummary(null);
    setTaskStatus(null);

    const measurementId = selectedMeasurement?.id ?? selectedMeasurementId;

    if (!measurementId) {
      setRunError(
        'Unable to determine the measurement ID. Please re-select it.',
      );
      setIsSubmitting(false);
      return;
    }

    const context: RunContext = {
      entityRef: selectedEntityRef,
      measurementId,
    };

    try {
      const refresh = await yardstickApi.refresh({
        entity: selectedEntityRef,
        measurements: [measurementId],
      });

      setPendingContext(context);
      setTaskStatus({
        id: refresh.taskId,
        status: 'submitted',
        measurements: [measurementId],
        entityRef: selectedEntityRef,
      });
      setActiveTaskId(refresh.taskId);
    } catch (error) {
      setIsSubmitting(false);
      setRunError(
        error instanceof Error
          ? error.message
          : 'Unable to start the Yardstick refresh task.',
      );
    }
  }, [
    selectedEntityRef,
    selectedMeasurementId,
    selectedMeasurement?.id,
    yardstickApi,
  ]);

  useEffect(() => {
    if (!activeTaskId) {
      return undefined;
    }

    let isMounted = true;

    const poll = async (intervalRef: {
      current: ReturnType<typeof setInterval> | undefined;
    }) => {
      try {
        const task = await yardstickApi.getTaskStatus(activeTaskId);
        if (!isMounted) {
          return;
        }

        setTaskStatus({
          id: task.id,
          status: task.status,
          entityRef: task.entityRef,
          measurements: task.measurements,
          startedAt: task.startedAt?.toString(),
          updatedAt: task.updatedAt?.toString(),
          expiresAt: task.expiresAt?.toString(),
        });

        if (['completed', 'error', 'expired'].includes(task.status)) {
          setIsSubmitting(false);
          setActiveTaskId(null);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }

          if (task.status === 'completed' && pendingContext) {
            await fetchMeasurementOutcome(pendingContext);
            setPendingContext(null);
          }

          if (task.status === 'error') {
            setRunError(
              'The Yardstick task ended with an error. Review the task logs and try again.',
            );
          }

          if (task.status === 'expired') {
            setRunError('The Yardstick task expired before finishing.');
          }
        }
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setIsSubmitting(false);
        setActiveTaskId(null);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        setRunError(
          error instanceof Error
            ? error.message
            : 'Failed to poll the Yardstick task.',
        );
      }
    };

    const intervalRef = {
      current: undefined as ReturnType<typeof setInterval> | undefined,
    };
    poll(intervalRef);
    intervalRef.current = setInterval(() => poll(intervalRef), 2500);

    return () => {
      isMounted = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [activeTaskId, yardstickApi, fetchMeasurementOutcome, pendingContext]);

  const renderIssuesList = () => {
    if (!resultSummary) {
      return null;
    }

    if (!resultSummary.issues.length) {
      return (
        <Typography variant="body2" color="textSecondary">
          No issues were returned for this measurement run.
        </Typography>
      );
    }

    return (
      <>
        <List dense>
          {resultSummary.issues.slice(0, MAX_ISSUES_TO_SHOW).map(issue => (
            <ListItem key={issue.id} divider>
              <ListItemText
                primary={issue.title ?? issue.message}
                secondary={`Severity: ${issue.severity}${
                  issue.dueDate?.text ? ` - due ${issue.dueDate.text}` : ''
                }`}
              />
            </ListItem>
          ))}
        </List>
        {resultSummary.issues.length > MAX_ISSUES_TO_SHOW && (
          <Typography variant="caption" color="textSecondary">
            Showing the first {MAX_ISSUES_TO_SHOW} issues. Use the Insights
            dashboard for the full list.
          </Typography>
        )}
      </>
    );
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={7}>
        <Paper style={{ padding: '24px' }}>
          <Typography variant="h6" gutterBottom>
            Choose an entity and measurement
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Use the catalog selector to identify which Backstage entity should
            be refreshed, then search for a Yardstick measurement. Measurements
            are grouped by the domains registered in YardstickBuilder.
          </Typography>
          <TextField
            select
            label="Catalog entity"
            value={selectedEntityRef}
            onChange={event => setSelectedEntityRef(event.target.value)}
            fullWidth
            variant="outlined"
            margin="normal"
            helperText="Entities are pulled directly from the Backstage catalog."
            disabled={catalogLoading}
          >
            {entityOptions.map(option => (
              <MenuItem key={option.ref} value={option.ref}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          {catalogLoading && (
            <Box display="flex" alignItems="center">
              <CircularProgress size={20} />
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ marginLeft: 8 }}
              >
                Loading catalog entities...
              </Typography>
            </Box>
          )}
          {catalogError && (
            <Alert severity="warning" style={{ marginTop: '8px' }}>
              Unable to fetch catalog entities. The default selection will be
              used.
            </Alert>
          )}
          <Autocomplete
            options={measurementOptions}
            getOptionLabel={option => `${option.title} (${option.id})`}
            groupBy={option => option.domainId ?? 'all-domains'}
            getOptionSelected={(option, value) => option.id === value.id}
            value={selectedMeasurement ?? null}
            onChange={(_, option) => setSelectedMeasurementId(option?.id ?? '')}
            disabled={measurementsLoading || measurementOptions.length === 0}
            renderInput={params => (
              <TextField
                {...params}
                label="Measurement"
                variant="outlined"
                margin="normal"
                placeholder="Search by measurement ID or name"
                helperText="Only active Yardstick measurements are listed."
              />
            )}
            noOptionsText={
              measurementsLoading
                ? 'Loading measurements...'
                : 'No measurements available'
            }
          />
          {measurementsLoading && (
            <Box display="flex" alignItems="center">
              <CircularProgress size={20} />
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ marginLeft: 8 }}
              >
                Loading Yardstick measurements...
              </Typography>
            </Box>
          )}
          {measurementsError && (
            <Alert severity="warning" style={{ marginTop: '8px' }}>
              Unable to load Yardstick measurements. Retry after refreshing the
              page.
            </Alert>
          )}
          <Box display="flex" justifyContent="flex-end" marginTop={2}>
            <Button
              color="primary"
              variant="contained"
              onClick={handleRunMeasurement}
              disabled={!canRunMeasurement}
            >
              {isSubmitting ? 'Starting task...' : 'Run measurement'}
            </Button>
          </Box>
        </Paper>
      </Grid>
      <Grid item xs={12} md={5}>
        <Paper style={{ padding: '24px' }}>
          <Typography variant="h6" gutterBottom>
            What to expect
          </Typography>
          <Typography variant="body2" paragraph>
            1. The tool submits a refresh request to YardstickBuilder for the
            selected entity and measurement.
          </Typography>
          <Typography variant="body2" paragraph>
            2. It polls <code>/v1/tasks/:id</code> until the task is completed
            (or errors/expires).
          </Typography>
          <Typography variant="body2" paragraph>
            3. Once completed, it fetches <code>/v1/results</code> for the
            entity/measurement pair so you can inspect the most recent checks
            and issues without leaving the Toolbox.
          </Typography>
        </Paper>
      </Grid>
      {runError && (
        <Grid item xs={12}>
          <Alert severity="error">{runError}</Alert>
        </Grid>
      )}
      {taskStatus && (
        <Grid item xs={12}>
          <Paper style={{ padding: '24px' }}>
            <Typography variant="h6" gutterBottom>
              Task status
            </Typography>
            {renderTaskStatusLabel(taskStatus.status)}
            <Grid container spacing={2} style={{ marginTop: '8px' }}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  Task ID
                </Typography>
                <Typography variant="body1">{taskStatus.id}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  Measurement(s)
                </Typography>
                <Typography variant="body1">
                  {taskStatus.measurements?.join(', ') ?? 'Unknown'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  Started
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(taskStatus.startedAt)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  Last update
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(taskStatus.updatedAt)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      )}
      {resultFetching && (
        <Grid item xs={12}>
          <Alert severity="info">
            Fetching refreshed measurement results...
          </Alert>
        </Grid>
      )}
      {resultSummary && (
        <Grid item xs={12}>
          <Paper style={{ padding: '24px' }}>
            <Typography variant="h6" gutterBottom>
              Latest measurement snapshot
            </Typography>
            <Typography variant="subtitle1">
              {resultSummary.measurementName} (
              {resultSummary.measurementCategory})
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Entity: {entityLabel(resultSummary.entityRef)}
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Run at: {formatDateTime(resultSummary.runAt)}
            </Typography>
            {renderOutcomeLabel(resultSummary.passed)}
            {resultSummary.missingData && (
              <Alert severity="warning" style={{ marginTop: '12px' }}>
                Yardstick did not return measurement data for this run. Confirm
                the measurement is active and has checks configured for the
                chosen entity.
              </Alert>
            )}
            {!!resultSummary.checks.length && (
              <>
                <Divider style={{ margin: '24px 0 16px' }} />
                <Typography variant="subtitle2" gutterBottom>
                  Check breakdown
                </Typography>
                <Grid container spacing={2}>
                  {resultSummary.checks.map(check => {
                    const status = getCheckStatusLabel(check.value);
                    const issueSuffix =
                      check.issuesCount > 0
                        ? ` - ${check.issuesCount} issue${
                            check.issuesCount === 1 ? '' : 's'
                          }`
                        : '';
                    return (
                      <Grid item xs={12} md={6} key={check.id}>
                        <Paper
                          variant="outlined"
                          style={{ padding: '12px', height: '100%' }}
                        >
                          <Typography variant="body1">{check.name}</Typography>
                          {check.message && (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              style={{ marginBottom: '4px' }}
                            >
                              {check.message}
                            </Typography>
                          )}
                          <Chip
                            label={`${status.label}${issueSuffix}`}
                            style={{
                              borderColor: status.color,
                              color: status.color,
                              borderWidth: 1,
                              borderStyle: 'solid',
                              background: 'transparent',
                            }}
                            size="small"
                          />
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </>
            )}
            <Divider style={{ margin: '24px 0 16px' }} />
            <Typography variant="subtitle2" gutterBottom>
              Issues
            </Typography>
            {renderIssuesList()}
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};
